
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function RecessedDownlightInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Recessed Downlight Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional recessed lighting and downlight installation in Fresno, CA. LED can lights, retrofit installations, and modern recessed lighting solutions.',
    url: `${siteUrl}/services/lighting-outdoor/recessed-downlight-installation`,
  };

  const features = [
    {
      icon: 'ri-flashlight-fill',
      title: 'LED Recessed Lights',
      description: 'Energy-efficient LED can lights that last 25+ years in Fresno homes',
    },
    { icon: 'ri-tools-line', title: 'New Construction Install', description: 'Recessed lighting installation for new builds and renovations' },
    { icon: 'ri-refresh-line', title: 'Retrofit Installation', description: 'Upgrade existing fixtures to modern LED recessed lights' },
    { icon: 'ri-layout-grid-line', title: 'Custom Layout Design', description: 'Strategic placement for optimal lighting coverage and ambiance' },
    { icon: 'ri-contrast-2-line', title: 'Dimmable Options', description: 'Install dimmable recessed lights with smart controls' },
    { icon: 'ri-fire-line', title: 'IC-Rated Fixtures', description: 'Safe insulation-contact rated lights for Fresno attic installations' },
  ];

  return (
    <>
      <SEOHead
        title="Recessed Lighting Installation Fresno CA | LED Downlights"
        description="Expert recessed lighting installation in Fresno, CA. LED can lights, retrofit installations, dimmable downlights, and custom layout design. Licensed electricians."
        canonical="/services/lighting-outdoor/recessed-downlight-installation"
        keywords="recessed lighting Fresno, downlight installation, LED can lights, retrofit lighting"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Recessed Downlight Installation — Fresno, CA"
        heroSubtitle="Modern, energy-efficient recessed lighting for Fresno homes and businesses"
        heroImage="https://public.readdy.ai/ai/img_res/66acdf0f40218eaa4fb540966b245522.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20modern%20LED%20recessed%20can%20light%20fixture%20installed%20in%20white%20ceiling%20with%20warm%20white%20light%20glowing%20professional%20electrical%20installation%20California%20home%20no%20people%20present&width=700&height=600&seq=recessed-light-fresno-002&orientation=portrait"
        fresnoContent="Upgrade your Fresno property with sleek, modern recessed lighting that provides clean, even illumination without bulky fixtures. From Fig Garden homes to Tower District renovations, we install energy-efficient LED recessed lights that reduce energy costs by up to 75% compared to traditional bulbs. Our licensed electricians design custom layouts that eliminate shadows and create the perfect ambiance for every room in your Central Valley home."
        features={features}
        faqs={[
          {
            question: 'How much does recessed lighting installation cost in Fresno?',
            answer:
              'Recessed lighting installation in Fresno typically costs $150–$300 per light including fixture, labor, and materials. For a standard room with 6–8 recessed lights, expect to pay $1,200–$2,400. Retrofit installations in existing ceilings are usually less expensive than new construction installations. LED fixtures cost slightly more upfront but save hundreds in energy costs over their 25+ year lifespan. We provide free consultations and detailed quotes for all Fresno projects.',
          },
          {
            question: 'What size recessed lights should I install in my Fresno home?',
            answer:
              'For Fresno homes, we typically recommend 4-inch recessed lights for smaller rooms and hallways, and 5–6 inch lights for living rooms, kitchens, and larger spaces. The size depends on ceiling height, room dimensions, and desired lighting effect. Standard 8-foot ceilings work well with 4-inch fixtures spaced 4–6 feet apart. Higher ceilings in newer Fresno homes may benefit from 6-inch fixtures for better light distribution. We assess your space and recommend the optimal size and spacing during consultation.',
          },
          {
            question: 'Can you install recessed lighting in existing ceilings in Fresno?',
            answer:
              'Yes! We specialize in retrofit recessed lighting installations for existing Fresno homes. Our electricians use remodel-rated fixtures designed for installation in finished ceilings without major demolition. We carefully cut openings, run wiring through attic spaces, and install IC-rated fixtures safe for contact with insulation. Most retrofit projects are completed in 1 day with minimal mess. Perfect for updating older Tower District homes or modernizing any Fresno property.',
          },
          {
            question: 'Are LED recessed lights worth it in Fresno?',
            answer:
              "Absolutely! LED recessed lights are ideal for Fresno's hot climate. They use 75% less energy than incandescent bulbs, produce minimal heat (important in Central Valley summers), and last 25+ years without replacement. A typical Fresno home with 20 LED recessed lights saves $200–$400 annually on electricity compared to old bulbs. LED fixtures also provide better light quality, are dimmable, and many are ENERGY STAR certified. The upfront investment pays for itself within 2–3 years.",
          },
          {
            question: 'How many recessed lights do I need in each room?',
            answer:
              'The number of recessed lights depends on room size and purpose. For Fresno homes, we typically recommend: Living rooms (200 sq ft) need 6–8 lights, kitchens need 8–12 lights for task and ambient lighting, bedrooms (150 sq ft) need 4–6 lights, and bathrooms need 2–4 lights. We space fixtures 4–6 feet apart for even coverage. During consultation, we assess your ceiling height, natural light, and usage to design the optimal layout for your Fresno property.',
          },
          {
            question: 'Do recessed lights require permits in Fresno?',
            answer:
              'Yes, recessed lighting installations in Fresno typically require electrical permits, especially when adding new circuits or modifying existing wiring. We handle all permit applications and inspections with the City of Fresno. Our licensed C-10 electricians ensure all installations meet California Title 24 energy standards and Fresno building codes. Proper permitting protects your home value and ensures insurance coverage. We make the permit process seamless for all Fresno customers.',
          },
        ]}
      />
    </>
  );
}
